/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  HStack, Icon, ICON_LAPTOP_ACCOUNT, ICON_MAP_MARKER_OUTLINE, Link, Message, Popover, Spacing, Stack, Text,
  UserPreview,
  UserPreviewSkeleton,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import PopoverPreview from '../../../components/popover-preview/PopoverPreview';
import { Applicant, HRInterview } from '../../../providers/api/dtos';
import PopoverPreviewSkeleton from '../../../components/popover-preview/PopoverPreview.skeleton';
import * as styles from './ViewHRInterviewPopoverPreview.style';
import HRInterviewAvatar from '../../components/hr-interview-avatar/HRInterviewAvatar';
import { useApplicant } from '../../../applicant/hooks/use-applicant/useApplicant';
import { jobApplicationAssigneeUsers, jobApplicationHrUsers } from '../../../job-application/constants';
import { JobApplicationType } from '../../../domain/types';
import { ViewAgentSmallPreview } from '../../../agent/components';
import useHRCalendarEvent from '../../hooks/use-hr-calendar-event/useHRCalendarEvent';
import { jobTitleLabels } from '../../../labels';

interface BaseViewHRInterviewPopoverPreviewProps extends React.AriaAttributes {
  trigger: React.ReactElement | ((params: {
    hrInterview?: HRInterview,
    isHRInterviewLoading: boolean,
    hrInterviewError?: Error,
  }) => React.ReactElement),
  applicationType: JobApplicationType,
  applicantId: NonNullable<Applicant['id']>,
}

export type ViewHRInterviewPopoverPreviewProps = BaseViewHRInterviewPopoverPreviewProps & ({
  hrInterviewId: NonNullable<HRInterview['id']>,
  hrInterview?: never,
} | {
  hrInterview: HRInterview,
  hrInterviewId?: never,
});

export const LOAD_INTERVIEW_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati del colloquio.';
export const LOAD_APPLICANT_DATA_ERROR_MESSAGE = 'Errore caricamento candidato.';

const ViewHRInterviewPopoverPreview: React.FC<ViewHRInterviewPopoverPreviewProps> = ({
  hrInterviewId,
  hrInterview: externalHRInterview,
  applicationType,
  applicantId,
  trigger,
  ...rest
}) => {
  const { data: loadedHRInterview, isLoading: isHRInterviewLoading, error: hrInterviewError } = useHRCalendarEvent(hrInterviewId);
  const hrInterview = (externalHRInterview || loadedHRInterview) as HRInterview;
  const { data: applicant, isLoading: isApplicantLoading, error: applicantError } = useApplicant(applicantId);

  const extraParticipant = React.useMemo(() => {
    if (!hrInterview?.extraParticipantEmail) return undefined;
    return jobApplicationHrUsers.find((hrUser) => hrUser.email === hrInterview?.extraParticipantEmail);
  }, [hrInterview]);

  const computedTrigger = React.useMemo(() => {
    if (typeof trigger === 'function') {
      return trigger({
        hrInterview,
        isHRInterviewLoading,
        hrInterviewError,
      });
    }

    return trigger;
  }, [trigger, hrInterview, isHRInterviewLoading, hrInterviewError]);

  return (
    <Popover
      trigger={computedTrigger}
      hasArrow
      {...rest}
    >
      <div css={styles.wrapper}>
        {(isHRInterviewLoading && !hrInterviewError) && (
          <PopoverPreviewSkeleton />
        )}
        {hrInterviewError && (
          <Spacing padding={200}>
            <Message
              type="critical"
              message={LOAD_INTERVIEW_DATA_ERROR_MESSAGE}
            />
          </Spacing>
        )}
        {(hrInterview) && (
          <PopoverPreview
            title={formatDate(new Date(hrInterview.date), { time: true })}
            details={hrInterview.isRemote ? 'Colloquio da remoto' : 'Colloquio di presenza'}
            avatar={(<HRInterviewAvatar size="L" />)}
          >
            <Stack gap={150}>
              {/** TODO: considerare se fare una ViewApplicantPreview come fatto per agent */}
              {applicant && (
                <UserPreview
                  name={applicant.name}
                  size="S"
                >
                  Candidato
                </UserPreview>
              )}
              {isApplicantLoading && (
                <UserPreviewSkeleton
                  aria-label="Caricamento dati candidato"
                  size="S"
                />
              )}
              {applicantError && (
                <UserPreview
                  name={LOAD_APPLICANT_DATA_ERROR_MESSAGE}
                  size="S"
                />
              )}
              {hrInterview.interviewerEmail && (
                <>
                  {applicationType === JobApplicationType.SALES && (
                    <ViewAgentSmallPreview
                      agentIdOrEmail={hrInterview.interviewerEmail}
                      details={(agent) => `Esaminatore - ${jobTitleLabels[agent.jobTitle]}`}
                      size="S"
                    />
                  )}
                  {applicationType !== JobApplicationType.SALES && (
                    <UserPreview
                      name={[...jobApplicationAssigneeUsers, ...jobApplicationHrUsers].find((hrUser) => hrUser.email === hrInterview?.interviewerEmail)?.name}
                      size="S"
                      aria-label="Anteprima esaminatore"
                    >
                      Esaminatore
                    </UserPreview>
                  )}
                </>
              )}
              {!hrInterview.interviewerEmail && (
                <UserPreview
                  name="Non assegnato"
                  size="M"
                  aria-label="Anteprima esaminatore non assegnato"
                >
                  Esaminatore
                </UserPreview>
              )}
              {extraParticipant && (
                <UserPreview
                  name={extraParticipant.name}
                  size="S"
                  aria-label="Partecipante HR"
                >
                  Partecipante - HR
                </UserPreview>
              )}
              {hrInterview.isRemote && hrInterview.meetingLink && (
                <HStack wrap="nowrap" gap={75}>
                  <Icon path={ICON_LAPTOP_ACCOUNT} size={20} />
                  <Text.Body>
                    <Link href={hrInterview.meetingLink} target="_blank">
                      {hrInterview.meetingLink}
                    </Link>
                  </Text.Body>
                </HStack>
              )}
              {!hrInterview.isRemote && hrInterview.address?.normalizedAddress && (
                <HStack wrap="nowrap" gap={75}>
                  <Icon path={ICON_MAP_MARKER_OUTLINE} size={20} />
                  <Text.Body>
                    {hrInterview.address.normalizedAddress}
                  </Text.Body>
                </HStack>
              )}
            </Stack>
          </PopoverPreview>
        )}
      </div>
    </Popover>
  );
};

export default ViewHRInterviewPopoverPreview;
